import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, RefreshCw, FlaskConical, ArrowRight } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const STATE_STYLE = {
  sent: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/30',
  pending: 'text-amber-300 bg-amber-400/10 border-amber-400/30',
  failed: 'text-red-300 bg-red-400/10 border-red-400/30',
};

export function TestPurchaseCard() {
  return (
    <section data-testid="admin-test-purchase" className="glass rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-5">
      <div className="h-12 w-12 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-300 shrink-0">
        <FlaskConical className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <h2 className="font-display font-bold text-lg">Test a live purchase</h2>
        <p className="text-sm text-white/55 mt-1">
          Buy a hidden $1 item that only admins can see. It runs the full real flow: console pick, terms, card or PayPal,
          your dashboard download and the Lucky Vault alert. Refund the $1 in Stripe or PayPal afterwards.
        </p>
      </div>
      <Link
        to="/product/admin-test"
        data-testid="admin-test-purchase-link"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-black font-semibold text-sm hover:shadow-[0_0_24px_rgba(245,184,61,0.5)] transition-shadow"
      >
        Start $1 test <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

export function VaultEventsSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get('/admin/vault-events').then((r) => setData(r.data)).catch(() => setData({ events: [] })), []);
  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setBusy(id);
    try {
      const { data: ev } = await api.post(`/admin/vault-events/${encodeURIComponent(id)}/retry`);
      toast[ev.state === 'sent' ? 'success' : 'error'](ev.state === 'sent' ? 'Alert delivered' : `Still ${ev.state} (HTTP ${ev.last_status})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Retry failed');
    } finally { setBusy(null); }
  };

  return (
    <section data-testid="vault-events-section">
      <div className="flex items-center gap-2 mb-4">
        <Radio className="h-5 w-5 text-brand" />
        <h2 className="font-display font-bold text-2xl">Lucky Vault alerts</h2>
        <button data-testid="vault-refresh-btn" onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>
      {data && !data.configured && (
        <p data-testid="vault-not-configured" className="mb-3 text-sm text-amber-300">Lucky Vault keys are missing, so alerts are being held until they're added.</p>
      )}
      <div className="glass rounded-2xl divide-y divide-white/5">
        {(data?.events || []).length === 0 && <div className="px-6 py-8 text-sm text-white/40" data-testid="vault-empty">No purchase alerts yet.</div>}
        {(data?.events || []).map((e) => (
          <div key={e.event_id} data-testid={`vault-event-${e.order_id}`} className="px-6 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className={`px-2.5 py-0.5 rounded-full border text-[11px] uppercase tracking-widest font-mono ${STATE_STYLE[e.state] || 'text-white/60 border-white/10'}`}>{e.state}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="text-white">{e.product_name} · <span className="font-mono">${e.amount} {e.currency}</span></div>
              <div className="text-xs text-white/40">{e.customer_email} · @{e.discord_username} · {e.payment_provider} · {new Date(e.created_at).toLocaleString()}</div>
            </div>
            <div className="text-xs text-white/40 font-mono">
              {e.attempts} attempt{e.attempts === 1 ? '' : 's'}{e.last_status ? ` · HTTP ${e.last_status}` : ''}{e.last_error && e.state !== 'sent' ? ` · ${e.last_error}` : ''}
            </div>
            {e.state !== 'sent' && (
              <button data-testid={`vault-retry-${e.order_id}`} onClick={() => retry(e.event_id)} disabled={busy === e.event_id}
                className="px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-50">
                {busy === e.event_id ? 'Sending…' : 'Retry now'}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
